import { Link } from "react-router-dom";
import { Code2, Copy, Download, Eye, ListOrdered } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { StatusChip, Tag } from "@/components/docs/StatusChip";
import { copyText, docUrl, formatSize } from "@/lib/docs";

const ModeToggle = ({ mode, setMode }) => (
  <div className="inline-flex rounded-md border border-border p-0.5" role="group" data-testid="doc-view-mode-toggle">
    {[
      ["rendered", "Prikaz", Eye],
      ["raw", "Izvor", Code2],
    ].map(([value, label, Icon]) => (
      <button
        key={value}
        type="button"
        onClick={() => setMode(value)}
        data-testid={`doc-view-mode-${value}-btn`}
        className={`inline-flex items-center gap-1.5 rounded px-2.5 py-1 text-xs transition-colors ${mode === value ? "bg-elevated text-foreground" : "text-muted-foreground hover:text-foreground"}`}
      >
        <Icon className="h-3.5 w-3.5" /> {label}
      </button>
    ))}
  </div>
);

export const DocHeader = ({ doc, text, mode, setMode, lineNumbers, setLineNumbers }) => {
  const isMd = doc.type === "md";
  const copy = async () => {
    await copyText(text);
    toast.success(`Kopirano: ${doc.path}`);
  };

  return (
    <header className="mb-8 border-b border-border pb-6" data-testid="doc-header">
      <nav className="font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground" data-testid="doc-breadcrumb">
        <Link to="/documentation" className="hover:text-foreground">G0</Link> <span className="mx-1.5">/</span> {doc.groupTitle}
      </nav>
      {!isMd && <h1 className="mt-3 font-heading text-3xl font-extrabold tracking-tight sm:text-4xl" data-testid="doc-title">{doc.title}</h1>}
      <p className="mt-3 max-w-3xl text-sm text-muted-foreground sm:text-base" data-testid="doc-summary">{doc.summary}</p>
      <div className="mt-5 flex flex-wrap items-center gap-2">
        <StatusChip status={doc.status} testId="doc-status-chip" />
        <Tag testId="doc-path-tag">/docs/{doc.path}</Tag>
        <Tag testId="doc-type-tag">{doc.type.toUpperCase()}</Tag>
        <Tag testId="doc-size-tag">{formatSize(text)}</Tag>
        <div className="flex w-full flex-wrap gap-2 sm:ml-auto sm:w-auto">
          {isMd ? (
            <ModeToggle mode={mode} setMode={setMode} />
          ) : (
            <Button variant="outline" size="sm" onClick={() => setLineNumbers(!lineNumbers)} data-testid="doc-line-numbers-toggle-btn">
              <ListOrdered /> {lineNumbers ? "Sakrij brojeve" : "Brojevi redaka"}
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={copy} data-testid="doc-copy-btn">
            <Copy /> Kopiraj
          </Button>
          <Button variant="outline" size="sm" asChild>
            <a href={docUrl(doc.path)} download={doc.path.split("/").pop()} data-testid="doc-download-btn">
              <Download /> Preuzmi
            </a>
          </Button>
        </div>
      </div>
    </header>
  );
};
